/**
 * HITL-1492 Pillar 3 (Part A / Capture) — the issue-report taxonomy.
 *
 * The category IS emitted on the wire alongside the type — see
 * `IssueReportEventData.issue_category`. It is also what the classification panel
 * GROUPS by, and the grouping is the point: a flat list is exactly what buried
 * `Unnecessary Staff Intervention` (last in the Slack form's scrolling dropdown,
 * and ~52% of all reports).
 *
 * HARDCODED ON PURPOSE (HITL-1501). There is no runtime override. Every category is
 * bound to a chip colour and a display label, so a config-supplied one would render
 * uncoloured and unlabelled. Adding a type is a release, not a flag flip.
 */

/**
 * Declaration order is load-bearing: `groupIssueTypesByCategory` renders groups in
 * this order.
 *
 * `storeBehavior` is FIRST, and that is the whole point rather than an accident.
 * `Unnecessary Staff Intervention` is ~52% of all reports; in any "natural" ordering
 * it lands near the bottom, which is precisely the failure this taxonomy exists to
 * fix. Putting it first keeps ordering a plain declaration rule — no ranker, no
 * scoring, nothing that reshuffles between flags.
 *
 * `readback` is SECOND (HITL-1501 follow-up). It is the one category that is not
 * attributable to a lane by construction — a wrong readback or a wrong total is the
 * same defect whether the AI or a human agent said it, and under HITL 3.0 co-pilot
 * they are increasingly the same voice. It earns the most visible slot after the 52%
 * value rather than sitting inside `ai_order`, which would assert an attribution the
 * agent cannot make. Consumers split it with `context.ai.is_ai_auto_mode`.
 *
 * `other` stays LAST so the escape hatch never precedes a real value.
 */
export enum IssueCategory {
  storeBehavior = 'store_behavior',
  readback = 'readback',
  aiEscalation = 'ai_escalation',
  aiOrder = 'ai_order',
  aiResponse = 'ai_response',
  asr = 'asr',
  audio = 'audio',
  pos = 'pos',
  menuConfig = 'menu_config',
  other = 'other',
  unclassified = 'unclassified',
}

export interface IssueTypeDefinition {
  /**
   * The wire value, emitted as `issue_type`.
   *
   * UNPREFIXED. The Slack form's values carried a family prefix (`AI - Missing Item`,
   * `ASR - Incorrect Transcription`) because a flat dropdown had nothing else to say
   * which lane a value belonged to. The payload now emits `issue_category` alongside
   * this, so the prefix would be the same fact twice — and the UI already renders
   * these under a category heading.
   *
   * ⚠️ CONSUMER NOTE: this means the values no longer join byte-exact to historical
   * Slack-form reports. Mapping old to new is mechanical and one-time —
   * `AI - Missing Item` becomes `{ issue_category: 'ai_order', issue_type: 'Missing
   * Item' }` — but it does have to be done. Nothing was ever emitted in the new shape
   * before this change, because the feature has never been enabled.
   */
  value: string;
  category: IssueCategory;
}

/**
 * Internal value OUTSIDE the taxonomy — the Slack form has no equivalent, and it is
 * deliberately absent from ISSUE_TYPES. A report is created
 * `Unclassified` on press and stays that way unless the agent classifies it inside
 * the flag window, so this is the absence of a choice rather than a choice: it is
 * never rendered as a selectable chip.
 *
 * TERMINAL, not "not yet triaged" (HITL-1501). The flag panel is the only
 * classification surface, so nothing arrives later to amend an unclassified report.
 * Consumers must not read `issue_type: null` as "a type is still coming".
 */
export const UNCLASSIFIED_ISSUE_TYPE = 'Unclassified';

/**
 * The single escape hatch. The Slack form had no equivalent, so HITL-1502 maps it per
 * workflow like any other type.
 *
 * Distinct from `Unclassified` on purpose. `Unclassified` means "nobody chose";
 * `Not listed` means "an agent looked and none of the 22 fit". Collapsing them loses
 * the difference between an unclassified report and a taxonomy gap — and the
 * alternative an agent reaches for under time pressure is the closest WRONG value,
 * which poisons the distribution undetectably.
 *
 * A detail is NOT required with it (HITL-1501): every chip commits and closes
 * identically, with no blocking branch anywhere. But `Not listed` is the only signal
 * that the taxonomy missed, and being global it does not say WHICH lane missed —
 * the category is the routing key and this one routes nowhere. So `issue_detail` is
 * the only clue to what the absent value should have been, which is why the panel
 * puts that field above the chips rather than at the bottom.
 */
export const NOT_LISTED_ISSUE_TYPE = 'Not listed';

/**
 * The two ASR values, named because the transcript gestures select them directly
 * (HITL-1501): a click on empty transcript files the first, a click on a word files
 * the second. Referenced rather than repeated as string literals so a rename cannot
 * silently leave those two entry points pointing at a value outside the taxonomy,
 * where `buildIssueCategoryMap` would resolve the category to null.
 */
export const ASR_MISSING_ISSUE_TYPE = 'Missing Transcription';
export const ASR_INCORRECT_ISSUE_TYPE = 'Incorrect Transcription';

/**
 * The detail the word-click gesture seeds into the flag panel, with the caret placed
 * at the end of it.
 *
 * A SENTENCE THE AGENT FINISHES, not a label: the misheard word is already known, so
 * the only thing left to capture is what the customer actually said. Ends in a space
 * so what they type reads on naturally.
 *
 * The word is UNQUOTED. Quoting it made the seed read as a formatted artifact rather
 * than a half-written sentence, and the agent then had to type around the closing
 * quote to correct the word itself.
 *
 * The panel compares the field against this seed on close and stays silent when they
 * are equal, so an unfinished prefill never emits a second revision pretending to be
 * agent input.
 */
export const buildAsrDetailSeed = (word: string): string =>
  `${word} should be `;

/**
 * The 23 values IN RENDER ORDER — grouped by category, `Not listed` last.
 *
 * Values are unprefixed (see `IssueTypeDefinition.value`); the family they belonged
 * to travels as `issue_category`.
 *
 * ## Why this is no longer Slack-form order
 *
 * It was, so that the mapping back to historical reports stayed positional. That
 * property is gone regardless: two values are renamed, `Incorrect Price` changes
 * lane, and four values are inserted between the originals. The old reports map by
 * VALUE, not by index, so the invariant was buying nothing while making the panel's
 * within-group order an emergent property of a list that did not look grouped.
 *
 * So this array is now the render order outright — `groupIssueTypesByCategory`
 * partitions it without reordering, and what you read here is what the agent sees.
 */
export const ISSUE_TYPES: IssueTypeDefinition[] = [
  {
    value: 'Unnecessary Staff Intervention',
    category: IssueCategory.storeBehavior,
  },

  // Source-agnostic by design — see the `readback` note on IssueCategory. Do NOT
  // move these under `ai_order`: the agent cannot tell whether the AI or a human
  // said the wrong number, and forcing the choice would put a guess on the wire.
  // `Incorrect Price` was in `ai_order` until HITL-1501; nothing was ever emitted
  // under that pairing, so no consumer has to remap it.
  { value: 'Wrong Readback', category: IssueCategory.readback },
  { value: 'Incorrect Price', category: IssueCategory.readback },
  // Separate from `Incorrect Price` because they fail differently: a wrong line
  // price is a menu/config defect, a wrong total with right prices is arithmetic,
  // tax or a discount. Collapsing them would need the detail field to tell them
  // apart, and that field is optional.
  { value: 'Incorrect Total', category: IssueCategory.readback },

  // Renamed from `Escalation`, which said only that an escalation happened — which
  // is not a defect. The defect is escalating when it was not warranted, and this
  // is the AI-side twin of `Unnecessary Staff Intervention`. `context.ai` carries
  // `is_escalation`, `is_escalated_on_car_enter` and `escalation_reason`, so
  // whether it really was unnecessary stays answerable server-side.
  { value: 'Unnecessary Escalation', category: IssueCategory.aiEscalation },

  { value: 'Missing Item', category: IssueCategory.aiOrder },
  { value: 'Incorrect Item', category: IssueCategory.aiOrder },
  { value: 'Incorrect Modification', category: IssueCategory.aiOrder },
  { value: 'Missing Modification', category: IssueCategory.aiOrder },
  { value: 'Upsell', category: IssueCategory.aiOrder },

  // The four response values are easy to confuse under time pressure, so they are
  // defined against each other rather than in isolation:
  //   Missing Response          — a turn that needed an answer got none.
  //   Incorrect Response        — it answered, and the answer was wrong.
  //   No Response               — the AI produced nothing at all, mid-conversation.
  //   Slow Response             — right answer, too late.
  //   Unable to Answer Question — it answered that it could not help. A DEFLECTION,
  //                               not silence and not a wrong answer; the AI knew it
  //                               was out of depth. Distinct because the fix is
  //                               knowledge coverage, not model or latency work.
  { value: 'Missing Response', category: IssueCategory.aiResponse },
  { value: 'Incorrect Response', category: IssueCategory.aiResponse },
  { value: 'No Response', category: IssueCategory.aiResponse },
  { value: 'Slow Response', category: IssueCategory.aiResponse },
  { value: 'Unable to Answer Question', category: IssueCategory.aiResponse },

  { value: ASR_MISSING_ISSUE_TYPE, category: IssueCategory.asr },
  { value: ASR_INCORRECT_ISSUE_TYPE, category: IssueCategory.asr },

  { value: 'Low Volume', category: IssueCategory.audio },
  { value: 'Choppy', category: IssueCategory.audio },

  // Renamed from `Validation`, which is what the CODE calls it. An agent reading
  // the panel mid-order cannot tell `Validation` from a menu problem; they can tell
  // that the POS rejected the order, because that is what they just watched happen.
  // The cause stays recoverable without asking them for it — `context.pos` carries
  // `cart_errors` with the nodePath of the rejected item, plus `order_error`.
  { value: 'Order Rejected by POS', category: IssueCategory.pos },

  // Its MEANING stays ambiguous (did the AI fail to auto-combo, or wrongly
  // auto-combo?). That is a question about what the value means, not where it
  // routes, so it does not reopen the category. An agent who means the second and
  // does not think this fits can pick `Not listed` with a detail.
  { value: 'Autocombo', category: IssueCategory.menuConfig },
  // The console and the store disagree about whether an item can be sold — it shows
  // available and staff says it is 86'd, or the reverse. Filed here rather than
  // under `pos` because the fix is menu configuration; a POS rejection the agent
  // actually saw is `Order Rejected by POS`. This also stops `menu_config` being an
  // Autocombo-only lane, which is what made the two impossible to tell apart.
  { value: 'Item Availability', category: IssueCategory.menuConfig },

  { value: NOT_LISTED_ISSUE_TYPE, category: IssueCategory.other },
];

/**
 * Flat `issue_type -> issue_category` map.
 */
export const buildIssueCategoryMap = (): Record<string, IssueCategory> =>
  ISSUE_TYPES.reduce((acc, { value, category }) => {
    acc[value] = category;
    return acc;
  }, {} as Record<string, IssueCategory>);

/**
 * Grouped by category, which is what the classification panel renders — not the flat
 * list. A flat 23-item list is exactly what buried `Unnecessary Staff Intervention`
 * (52% of all reports), so grouping is the fix, not decoration.
 *
 * Category order follows IssueCategory declaration order (storeBehavior first,
 * readback second, other last); within a category, ISSUE_TYPES order — which is
 * already grouped, so this partitions without reordering. Empty categories are
 * omitted, and
 * `unclassified` never appears because it is not a real choice. `other` DOES appear —
 * `Not listed` is a real choice — and its declaration position puts it last.
 */
export const groupIssueTypesByCategory = (): Array<{
  category: IssueCategory;
  types: IssueTypeDefinition[];
}> =>
  (Object.values(IssueCategory) as IssueCategory[])
    .filter((category) => category !== IssueCategory.unclassified)
    .map((category) => ({
      category,
      types: ISSUE_TYPES.filter((type) => type.category === category),
    }))
    .filter((group) => group.types.length > 0);
